/**
 * FachpersonSignupForm — Therapeut-Registrierung mit Org-Daten.
 * Extrahiert aus `pages/Login.tsx` (UX-Audit Phase 5).
 *
 * Zeigt die BSI-Passwort-Regeln als Live-Vorschau (PasswordRulesList).
 * Org-Name-Platzhalter ist je nach Mode unterschiedlich (Praxis vs.
 * Klinik); der Container reicht den aktiven Mode durch.
 */

import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import type { SettingType } from '@/hooks/useFeatures';
import {
  fachpersonSignupSchema,
  type FachpersonSignupInput,
} from '@/schemas/auth';
import { PasswordRulesList } from './PasswordRulesList';

interface Props {
  mode: SettingType | null;
  onSubmit: (values: FachpersonSignupInput) => Promise<void>;
}

export function FachpersonSignupForm({ mode, onSubmit }: Props) {
  const form = useForm<FachpersonSignupInput>({
    resolver: zodResolver(fachpersonSignupSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      orgName: '',
      agbAccepted: false as unknown as true,
    },
  });

  const password = form.watch('password');
  const orgNamePlaceholder =
    mode === 'klinik' ? 'z. B. Klinikum München' : 'z. B. Ernährungspraxis München';
  const orgNameLabel = mode === 'klinik' ? 'Einrichtungsname' : 'Praxisname';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-xs text-muted-foreground">Vorname</FormLabel>
                <FormControl>
                  <Input
                    className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-xs text-muted-foreground">Nachname</FormLabel>
                <FormControl>
                  <Input
                    className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="orgName"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">{orgNameLabel}</FormLabel>
              <FormControl>
                <Input
                  placeholder={orgNamePlaceholder}
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">E-Mail</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="praxis@beispiel.de"
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">Passwort</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <PasswordRulesList password={password ?? ''} />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="agbAccepted"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-start gap-2.5 pt-1">
                <FormControl>
                  <Checkbox
                    id="agb-accept"
                    checked={field.value === true}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    className="mt-0.5"
                  />
                </FormControl>
                <label htmlFor="agb-accept" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                  Ich akzeptiere die{' '}
                  <Link to="/agb" target="_blank" className="text-primary underline hover:text-primary/80">AGB</Link>
                  {' '}und habe die{' '}
                  <Link to="/datenschutz" target="_blank" className="text-primary underline hover:text-primary/80">Datenschutzerklärung</Link>
                  {' '}gelesen.
                </label>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-10"
        >
          {form.formState.isSubmitting && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Registrieren
        </Button>
      </form>
    </Form>
  );
}
